import { useState, useRef } from 'react';
import { Check, Loader2, Building2, Stethoscope, Megaphone, ChevronDown, Send, Handshake, ArrowRight, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '601165337469';

const COLLAB_TYPES = [
  { key: 'brand_business', icon: Building2, labelKey: 'collab.type_brand', descKey: 'collab.type_brand_desc' },
  { key: 'doctor_physician', icon: Stethoscope, labelKey: 'collab.type_doctor', descKey: 'collab.type_doctor_desc' },
  { key: 'influencer_kol', icon: Megaphone, labelKey: 'collab.type_influencer', descKey: 'collab.type_influencer_desc' },
];

const MESSAGE_PLACEHOLDERS = {
  brand_business: 'collab.msg_placeholder_brand',
  doctor_physician: 'collab.msg_placeholder_doctor',
  influencer_kol: 'collab.msg_placeholder_influencer',
};

const countryCodes = [
  { code: '+60', label: 'MY' },
  { code: '+65', label: 'SG' },
  { code: '+62', label: 'ID' },
  { code: '+66', label: 'TH' },
  { code: '+63', label: 'PH' },
  { code: '+84', label: 'VN' },
  { code: '+91', label: 'IN' },
  { code: '+86', label: 'CN' },
  { code: '+81', label: 'JP' },
  { code: '+44', label: 'UK' },
  { code: '+1', label: 'US' },
  { code: '+61', label: 'AU' },
];

export const CollaborationSection = () => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', company: '', message: '' });
  const [countryCode, setCountryCode] = useState('+60');
  const [showCodeDropdown, setShowCodeDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const dropdownRef = useRef(null);
  const formRef = useRef(null);

  const isZh = i18n.language?.startsWith('zh');
  const waMsg = encodeURIComponent(isZh
    ? '您好，我对与 VERSE Clinic 的合作机会感兴趣。'
    : 'Hi, I am interested in partnership opportunities with VERSE Clinic.');

  const perks = [
    t('collab.perk1'),
    t('collab.perk2'),
    t('collab.perk3'),
  ];

  const handleExpand = () => {
    setExpanded(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (apiError) setApiError('');
  };

  const validate = () => {
    const errs = {};
    if (!selectedType) errs.collab_type = t('collab.error_type');
    if (!formData.full_name || formData.full_name.trim().length < 2) errs.full_name = t('collab.error_name');
    const emailPattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim() || !emailPattern.test(formData.email.trim())) errs.email = t('collab.error_email');
    const phoneDigits = formData.phone.replace(/[\s\-()+ ]/g, '');
    if (!formData.phone || phoneDigits.length < 7) errs.phone = t('collab.error_phone');
    if (!formData.company || formData.company.trim().length < 1) errs.company = t('collab.error_company');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setIsSubmitting(true);

    try {
      let fullPhone = formData.phone.trim();
      if (!fullPhone.startsWith('+')) fullPhone = countryCode + fullPhone;

      const payload = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        phone: fullPhone,
        company: formData.company.trim(),
        collab_type: selectedType,
        message: formData.message.trim() || null,
      };

      const res = await fetch(`${BACKEND_URL}/api/collaborations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok && data.ok) {
        setIsSuccess(true);
      } else {
        setApiError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = (field) => ({
    backgroundColor: '#ffffff',
    color: 'var(--vc-ink)',
    borderColor: errors[field] ? 'var(--vc-danger)' : '#e2e0db',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
  });

  const focusHandler = (field) => ({
    onFocus: (e) => {
      if (!errors[field]) e.currentTarget.style.borderColor = 'var(--vc-gold)';
      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(214,168,74,0.25)';
    },
    onBlur: (e) => {
      if (!errors[field]) e.currentTarget.style.borderColor = '#e2e0db';
      e.currentTarget.style.boxShadow = 'none';
    },
  });

  const selectedCountry = countryCodes.find((c) => c.code === countryCode) || countryCodes[0];
  const msgPlaceholder = selectedType ? t(MESSAGE_PLACEHOLDERS[selectedType]) : t('collab.placeholder_message');

  return (
    <section
      id="collaborate"
      data-testid="collaboration-section"
      className="py-16 lg:py-24 px-6 sm:px-8"
      style={{ backgroundColor: 'var(--vc-bg-2)' }}
    >
      <div className="max-w-[800px] mx-auto">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-14">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
            style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}
          >
            {t('collab.badge')}
          </p>
          <h2
            className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-[34px]"
            style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg)' }}
          >
            {t('collab.title')}
          </h2>
          <p
            className="mt-4 text-sm sm:text-base leading-relaxed max-w-lg mx-auto"
            style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
          >
            {t('collab.subtitle')}
          </p>
          {/* Trust indicator */}
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{
            backgroundColor: 'rgba(214,168,74,0.08)', border: '1px solid rgba(214,168,74,0.2)',
          }}>
            <Handshake className="w-3.5 h-3.5" style={{ color: 'var(--vc-gold)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
              {t('collab.trust_badge')}
            </span>
          </div>
        </div>

        {/* Card */}
        <div
          ref={formRef}
          className="rounded-[24px] relative"
          style={{ backgroundColor: 'var(--vc-card-light)', boxShadow: 'var(--vc-shadow-form)' }}
        >
          {/* ─── Collapsed Teaser State ─── */}
          {!expanded && !isSuccess && (
            <div className="p-5 sm:p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(214,168,74,0.1)', border: '1px solid rgba(214,168,74,0.25)' }}>
                  <Handshake className="w-5 h-5" style={{ color: 'var(--vc-gold)' }} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}>
                  {t('collab.teaser_title')}
                </h3>
              </div>

              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                {t('collab.teaser_desc')}
              </p>

              {/* Partnership Perks */}
              <ul className="space-y-2.5 mb-6">
                {perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                    <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(214,168,74,0.12)', border: '1px solid rgba(214,168,74,0.25)' }}>
                      <Check className="w-3 h-3" style={{ color: 'var(--vc-gold)' }} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>

              {/* Expand CTA */}
              <button
                data-testid="collab-expand-button"
                onClick={handleExpand}
                className="w-full h-12 rounded-xl font-medium transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
                style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold)'}
              >
                {t('collab.cta_explore')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ─── Expanded Form State ─── */}
          {expanded && !isSuccess && (
            <div style={{ animation: 'slideInRight 0.3s ease-out forwards' }}>
              <form onSubmit={handleSubmit} className="p-5 sm:p-8 lg:p-10">
                {/* Header with collapse */}
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg sm:text-xl font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}>
                    {t('collab.form_title')}
                  </h3>
                  <button
                    type="button"
                    data-testid="collab-collapse-button"
                    onClick={() => setExpanded(false)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200"
                    style={{ color: 'var(--vc-ink-muted)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs mb-5" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                  {t('collab.form_subtitle')}
                </p>

                {/* Collaboration Type Selection */}
                <p className="text-sm font-medium mb-2.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                  {t('collab.select_type')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                  {COLLAB_TYPES.map((ct) => {
                    const Icon = ct.icon;
                    const isActive = selectedType === ct.key;
                    return (
                      <button
                        key={ct.key}
                        type="button"
                        data-testid={`collab-type-${ct.key}`}
                        onClick={() => {
                          setSelectedType(ct.key);
                          if (errors.collab_type) setErrors((prev) => ({ ...prev, collab_type: undefined }));
                        }}
                        className="rounded-xl p-3.5 text-left transition-all duration-200 border"
                        style={{
                          backgroundColor: isActive ? 'rgba(214,168,74,0.08)' : '#ffffff',
                          borderColor: isActive ? 'var(--vc-gold)' : errors.collab_type ? 'var(--vc-danger)' : '#e2e0db',
                        }}
                      >
                        <Icon
                          className="w-4.5 h-4.5 mb-1.5"
                          style={{ color: isActive ? 'var(--vc-gold)' : '#9ca3af', width: '18px', height: '18px' }}
                        />
                        <p className="text-sm font-medium" style={{ color: isActive ? 'var(--vc-gold)' : 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                          {t(ct.labelKey)}
                        </p>
                        <p className="text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                          {t(ct.descKey)}
                        </p>
                      </button>
                    );
                  })}
                </div>
                {errors.collab_type && <p className="-mt-3 mb-4 text-xs" style={{ color: 'var(--vc-danger)', fontFamily: 'Inter, sans-serif' }}>{errors.collab_type}</p>}

                {/* Form Fields */}
                <div className="space-y-4">
                  {/* Name + Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                        {t('collab.label_name')}
                      </label>
                      <input
                        data-testid="collab-name-input"
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        placeholder={t('collab.placeholder_name')}
                        className="w-full h-12 px-4 rounded-xl border outline-none transition-colors duration-200"
                        style={inputStyle('full_name')}
                        {...focusHandler('full_name')}
                      />
                      {errors.full_name && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)' }}>{errors.full_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                        {t('collab.label_email')}
                      </label>
                      <input
                        data-testid="collab-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder={t('collab.placeholder_email')}
                        className="w-full h-12 px-4 rounded-xl border outline-none transition-colors duration-200"
                        style={inputStyle('email')}
                        {...focusHandler('email')}
                      />
                      {errors.email && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)' }}>{errors.email}</p>}
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                      {t('collab.label_company')}
                    </label>
                    <input
                      data-testid="collab-company-input"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder={t('collab.placeholder_company')}
                      className="w-full h-12 px-4 rounded-xl border outline-none transition-colors duration-200"
                      style={inputStyle('company')}
                      {...focusHandler('company')}
                    />
                    {errors.company && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)' }}>{errors.company}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                      {t('collab.label_phone')}
                    </label>
                    <div className="flex items-stretch">
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          data-testid="collab-country-code"
                          onClick={() => setShowCodeDropdown(!showCodeDropdown)}
                          className="flex items-center gap-1 h-12 px-2 sm:px-3 rounded-l-xl border border-r-0 transition-colors duration-200"
                          style={{ backgroundColor: '#f7f6f3', borderColor: errors.phone ? 'var(--vc-danger)' : '#e2e0db', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500, minWidth: '72px' }}
                        >
                          <span>{selectedCountry.label}</span>
                          <span>{selectedCountry.code}</span>
                          <ChevronDown className="w-3 h-3 ml-0.5" style={{ color: '#9ca3af' }} />
                        </button>
                        {showCodeDropdown && (
                          <div className="absolute top-full left-0 mt-1 rounded-xl border overflow-hidden z-50" style={{ backgroundColor: '#ffffff', borderColor: '#e2e0db', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: 'min(200px, calc(100vw - 48px))', maxHeight: '200px', overflowY: 'auto' }}>
                            {countryCodes.map((cc) => (
                              <button
                                key={cc.code}
                                type="button"
                                onClick={() => { setCountryCode(cc.code); setShowCodeDropdown(false); }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm"
                                style={{ fontFamily: 'Inter, sans-serif', color: cc.code === countryCode ? 'var(--vc-gold)' : 'var(--vc-ink)', backgroundColor: cc.code === countryCode ? 'rgba(214,168,74,0.08)' : 'transparent' }}
                                onMouseEnter={(e) => { if (cc.code !== countryCode) e.currentTarget.style.backgroundColor = '#f7f6f3'; }}
                                onMouseLeave={(e) => { if (cc.code !== countryCode) e.currentTarget.style.backgroundColor = 'transparent'; }}
                              >
                                <span className="font-medium w-8">{cc.label}</span>
                                <span className="ml-auto font-medium">{cc.code}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        data-testid="collab-phone-input"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="12 345 6789"
                        className="w-full h-12 px-4 rounded-r-xl border outline-none transition-colors duration-200"
                        style={inputStyle('phone')}
                        {...focusHandler('phone')}
                      />
                    </div>
                    {errors.phone && <p className="mt-1.5 text-xs" style={{ color: 'var(--vc-danger)' }}>{errors.phone}</p>}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}>
                      {t('collab.label_message')} <span style={{ color: 'var(--vc-ink-muted)', fontWeight: 400 }}>({t('collab.optional')})</span>
                    </label>
                    <textarea
                      data-testid="collab-message-input"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder={msgPlaceholder}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border outline-none transition-colors duration-200 resize-none"
                      style={{ ...inputStyle('message'), fontSize: '14px' }}
                      {...focusHandler('message')}
                    />
                  </div>
                </div>

                {apiError && (
                  <div data-testid="collab-error" className="mt-4 p-3 rounded-xl text-sm" style={{ backgroundColor: 'rgba(239,68,68,0.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)', fontFamily: 'Inter, sans-serif' }}>
                    {apiError}
                  </div>
                )}

                {/* Submit */}
                <button
                  data-testid="collab-submit-button"
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 w-full h-12 rounded-xl font-medium transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 text-sm"
                  style={{ backgroundColor: 'var(--vc-gold)', color: 'var(--vc-ink)', fontFamily: 'Inter, sans-serif' }}
                  onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'; }}
                  onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--vc-gold)'; }}
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />{t('collab.submitting')}</>
                  ) : (
                    <><Send className="w-4 h-4" />{t('collab.submit')}</>
                  )}
                </button>

                {/* WhatsApp Direct */}
                <div className="mt-4 flex items-center justify-center">
                  <a
                    data-testid="collab-whatsapp-link"
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-medium transition-colors duration-200"
                    style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--vc-gold)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--vc-ink-muted)'}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {t('collab.whatsapp_cta')}
                  </a>
                </div>
              </form>
            </div>
          )}

          {/* ─── Success State ─── */}
          {isSuccess && (
            <div data-testid="collab-success" className="p-5 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-center" style={{ minHeight: '340px' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: 'rgba(214,168,74,0.15)', border: '1px solid rgba(214,168,74,0.45)' }}>
                <Check className="w-7 h-7" style={{ color: 'var(--vc-gold)' }} />
              </div>
              <h3 className="mt-6 text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-ink)' }}>
                {t('collab.success_title')}
              </h3>
              <p className="mt-3 text-base leading-relaxed max-w-sm" style={{ color: 'var(--vc-ink-muted)', fontFamily: 'Inter, sans-serif' }}>
                {t('collab.success_body')}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
